import {
  // ButtonDS,
  DataCard,
  Spacer,
  // TextCapitalized,
  triggerToast,
} from "@qivia/ui";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { MultipleDrivers /*, PeopleAlt */ } from "@qivia/ui/src/assets/assets";
import { DriversToSendFormModal } from "./modals/driversToSendFormModal";
import { VehiclesContentHeader } from "./vehiclesHeader";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  selectDriversList,
  driversListAsync,
  selectDriversListStatus,
} from "../drivers/driversSlice";
import { selectCompany } from "../homeSlice";
import {
  driverElectroCompatibilityFormsAsync,
  selectDriverElectroCompatibilityFormsStatus,
  getDriverElectroCompatibilityFormsAsync,
} from "./electrificationSlice";
import { DriversListFormDisplayedRow } from "./electrificationApi";
import {
  listAllVehiclesInDbAsync,
  selectListAllVehiclesInDbStatus,
} from "../vehicles/vehiclesSlice";
import "../../../i18n/i18n";

type DriversHeaderType =
  | "superElectroCompatible"
  | "averageElectroCompatibleScore"
  | "shareOfDriversAnalyzed";

const driversHeaderDatas: DriversHeaderType[] = [
  "superElectroCompatible",
  "averageElectroCompatibleScore",
  "shareOfDriversAnalyzed",
];

export const ElectrificationHeader = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const drivers = useAppSelector(selectDriversList);
  const company = useAppSelector(selectCompany);
  const driverElectroCompatibilityFormsStatus = useAppSelector(
    selectDriverElectroCompatibilityFormsStatus,
  );
  const driversListStatus = useAppSelector(selectDriversListStatus);
  const listAllVehiclesInDbStatus = useAppSelector(
    selectListAllVehiclesInDbStatus,
  );
  const [isModalDriversFormVisible, setIsModalDriversFormVisible] =
    useState<boolean>(false);

  const onSubmit = useCallback(
    (response: DriversListFormDisplayedRow[]) => {
      if (!company) {
        return;
      }
      const drivers = response.map((driver) => ({
        uuid: driver.uuid ?? "",
        phone: driver.phone ?? "",
      }));
      void dispatch(
        driverElectroCompatibilityFormsAsync({
          drivers,
          company: company.uuid,
        }),
      );
    },
    [company, dispatch],
  );

  useEffect(() => {
    if (driverElectroCompatibilityFormsStatus === "success") {
      triggerToast(
        t("electrification.header.title.survey.success") || "",
        "valid",
      );
    }
    if (driverElectroCompatibilityFormsStatus === "failed") {
      triggerToast(
        t("electrification.header.title.survey.failed") || "",
        "error",
      );
    }
  }, [dispatch, driverElectroCompatibilityFormsStatus, t]);

  useEffect(() => {
    if (company && driversListStatus === "idle") {
      void dispatch(driversListAsync(company.uuid));
    }
  }, [company, dispatch, driversListStatus]);

  useEffect(() => {
    if (company && listAllVehiclesInDbStatus === "idle") {
      void dispatch(listAllVehiclesInDbAsync({ companyUuid: company.uuid }));
    }
  }, [company, dispatch, listAllVehiclesInDbStatus]);

  useEffect(() => {
    if (company && driverElectroCompatibilityFormsStatus === "idle") {
      void dispatch(getDriverElectroCompatibilityFormsAsync());
    }
  }, [company, dispatch, driverElectroCompatibilityFormsStatus]);

  if (!params.tab) {
    return;
  }

  if (params.tab === "vehicles") {
    return <VehiclesContentHeader />;
  }

  return (
    <StyledDriversHeader>
      {/* "Electro-compatible drivers" banner hidden (QIV-7659) — uncomment the block below (and the related imports/styled) to show it again.
      <StyledBanner>
        <Spacer y={1.5} />
        <StyledRow>
          <Spacer x={1.5} />
          <StyledBannerInfo>
            <PeopleAlt />
            <Spacer x={1} />
            <StyledColumn>
              <StyledBannerTitle>
                <TextCapitalized>
                  {t(`electrification.header.title.${params.tab}`)}{" "}
                  {t(`electrification.header.title.${params.tab}.part2`)}
                </TextCapitalized>
              </StyledBannerTitle>
              <Spacer y={0.5} />
              <StyledSubtitle>
                <TextCapitalized>
                  {t(`electrification.header.subtitle.${params.tab}`)}
                </TextCapitalized>
              </StyledSubtitle>
            </StyledColumn>
          </StyledBannerInfo>
          <Spacer x={1} />
          <StyledButtonDiv>
            <ButtonDS
              text={t("electrification.header.title.survey")}
              format={"hug"}
              buttonType={"primary"}
              sizeButton={"S"}
              // TODO: re-enable survey launch (temporarily disabled)
              onClick={() => {
                // setIsModalDriversFormVisible(true);
              }}
            />
          </StyledButtonDiv>
          <Spacer x={1.5} />
        </StyledRow>
        <Spacer y={1.5} />
      </StyledBanner>
      <Spacer y={1.5} />
      */}
      <DriversContentHeader />
      <DriversToSendFormModal
        visible={isModalDriversFormVisible}
        onCloseModal={() => setIsModalDriversFormVisible(false)}
        drivers={drivers}
        onSubmit={onSubmit}
      />
    </StyledDriversHeader>
  );
};

// TO REMOVE LATER — mocked drivers matching the Conducteurs table (driversTab),
// so the header figures stay coherent with the rows shown.
const fakeDrivers = [
  { electroCompatible: true, prioritizationScore: 88, drivingScore: 82 },
  { electroCompatible: true, prioritizationScore: 55, drivingScore: 61 },
  { electroCompatible: false, prioritizationScore: 24, drivingScore: 30 },
  { electroCompatible: true, prioritizationScore: 72, drivingScore: 45 },
  { electroCompatible: false, prioritizationScore: 90, drivingScore: 78 },
];

const electroCompatibleCount = fakeDrivers.filter(
  (d) => d.electroCompatible,
).length;

const fakeDriversHeaderData: Record<DriversHeaderType, number> = {
  superElectroCompatible: electroCompatibleCount,
  averageElectroCompatibleScore: Math.round(
    fakeDrivers.reduce(
      (sum, d) => sum + (d.prioritizationScore + d.drivingScore) / 2,
      0,
    ) / fakeDrivers.length,
  ),
  shareOfDriversAnalyzed: Math.round(
    (electroCompatibleCount / fakeDrivers.length) * 100,
  ),
};

const DriversContentHeader = () => {
  const { t } = useTranslation();

  return (
    <StyledFlex>
      {driversHeaderDatas.map((v, index) => (
        <Fragment key={v}>
          {index !== 0 && <Spacer x={1} />}
          <StyledCardWrapper>
            <DataCard
              titleHeight=""
              titleElement={<MultipleDrivers />}
              title={
                <StyledCount>
                  {fakeDriversHeaderData[v]}
                  {t(`electrification.header.${v}.unit`)}
                </StyledCount>
              }
              backgroundColor="white"
              content={
                <StyledCardLabel>
                  {t(`electrification.header.${v}`)}
                </StyledCardLabel>
              }
            />
          </StyledCardWrapper>
        </Fragment>
      ))}
    </StyledFlex>
  );
};

const StyledDriversHeader = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

/* Styled components of the hidden "Electro-compatible drivers" banner (QIV-7659) — uncomment together with the banner.
const StyledBanner = styled.div`
  display: flex;
  flex-direction: column;
  background: ${colors["colors/surfaces/background/background_level1"]};
  border-radius: 0.75rem;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
`;

const StyledBannerInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledBannerTitle = styled.div`
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]};
`;

const StyledSubtitle = styled.div`
  ${typographies["Body/S"]}
`;

const StyledButtonDiv = styled.div`
  display: flex;
  flex-shrink: 0;
`;
*/

const StyledFlex = styled.div`
  display: flex;
`;

const StyledCardWrapper = styled.div`
  flex: 1;
  display: flex;
`;

const StyledCount = styled.span`
  ${typographies["Header/H1"]}
  color: ${colors["colors/text/black"]};
`;

const StyledCardLabel = styled.span`
  ${typographies["Body/M"]}
  color: ${colors["colors/text/darkGrey"]};
`;
